import { useCallback, useEffect, useRef } from 'react';
import { appBuildManager } from '../services/AppBuildManager';
import { useVisibilityAwarePolling } from './useVisibilityAwarePolling';

interface VersionInfo {
    buildId: string;
    buildTime: string;
    version: string;
    /** Full git SHA of the deployed web build. Written by the versionPlugin in vite.config.ts. */
    gitHashFull?: string;
}

/**
 * Polls `/version.json` and reports the deployed web build sha to AppBuildManager, which decides
 * whether this tab is running stale code. Reacting to that (reload or banner) is the job of
 * components/StaleBuildReloader.tsx -- this hook only observes.
 *
 * This is the signal that covers a tab making NO API calls at all. The other signal, the
 * X-Calculist-Git-Commit header read in BaseApiService, only fires when the user does something.
 * Both are needed; neither replaces the other.
 *
 * Two things about the previous implementation caused a phone left open overnight to miss a
 * deploy entirely, and both are fixed here:
 *
 *  1. It compared the server's buildId against the FIRST buildId it managed to fetch, not against
 *     the bundle actually running. A tab whose first successful check happened after the deploy
 *     adopted the new id as its baseline and never reloaded. We now compare against
 *     `__APP_GIT_COMMIT_SHA__`, baked in at build time, so there is no baseline to get wrong.
 *  2. Its hand-rolled interval was skipped whenever the tab was hidden, and mobile browsers freeze
 *     background timers regardless. useVisibilityAwarePolling with `runOnVisible` re-checks the
 *     instant the tab is shown again, and the `pageshow` listener below covers bfcache restores,
 *     where `visibilitychange` is not guaranteed to fire.
 *
 * @param checkInterval - How often to check while the tab is visible, in ms (default 30s)
 * @param enabled - Whether version checking is enabled (always off in development)
 */
export function useVersionCheck(checkInterval: number = 30000, enabled: boolean = true) {
    const isCheckingRef = useRef<boolean>(false);

    // Disabled in dev: vite serves no built version.json, and __APP_GIT_COMMIT_SHA__ is a local sha.
    const active = enabled && !import.meta.env.DEV;

    const checkVersion = useCallback(async () => {
        // Prevent overlapping checks. Once stale there is nothing left to learn.
        if (isCheckingRef.current || appBuildManager.isStale()) {
            return;
        }

        try {
            isCheckingRef.current = true;

            // Cache-bust: version.json is served no-store (public/staticwebapp.config.json), but
            // the query param also defeats any intermediate cache.
            const response = await fetch(`/version.json?t=${Date.now()}`, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                },
            });

            if (!response.ok) {
                // version.json doesn't exist yet (first build) -- nothing to compare.
                return;
            }

            // Guard against the SPA navigation fallback handing us index.html instead of JSON.
            // Treating an HTML body as a version would be a spurious "stale" verdict.
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                return;
            }

            const versionInfo: VersionInfo = await response.json();
            appBuildManager.observeServerSha(versionInfo?.gitHashFull);
        } catch (error) {
            // Best-effort: a failed check must never interrupt the user. The next tick retries.
            console.debug('[VersionCheck] Error checking version:', error);
        } finally {
            isCheckingRef.current = false;
        }
    }, []);

    // Poll while visible, and re-check immediately whenever the tab becomes visible again.
    useVisibilityAwarePolling(checkVersion, checkInterval, { enabled: active, runOnVisible: true });

    useEffect(() => {
        if (!active) {
            return;
        }

        // Initial check, delayed slightly so it doesn't compete with the initial page load.
        const initialTimeout = setTimeout(() => {
            void checkVersion();
        }, 2000);

        // A tab restored from the back/forward cache resumes with whatever bundle it had. Safari
        // and mobile Chrome restore this way routinely and do not reliably fire visibilitychange.
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                void checkVersion();
            }
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => {
            clearTimeout(initialTimeout);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [active, checkVersion]);
}
